import { useState } from 'react';
import { Video, Heart, Camera, ArrowLeft } from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import stoneGoldBg from '../../assets/images/stone-gold-black-bg.png';
import DataPrivacyModal from './DataPrivacyModal';

function AttractorScreen({ event, onStart }) {
  const [selectedMode, setSelectedMode] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleSelectMode = (mode) => {
    setSelectedMode(mode);
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onStart(
      selectedMode || 'video',
      guestName.trim() || 'Anonymous',
      guestEmail.trim()
    );
  };

  const eventDate = event.eventDate?.toDate
    ? formatDate(event.eventDate.toDate())
    : formatDate(new Date(event.eventDate));

  const resetGuestForm = () => {
    setShowForm(false);
    setSelectedMode(null);
    setGuestName('');
    setGuestEmail('');
  };

  if (showForm) {
    const isPhotoMode = selectedMode === 'photo';

    return (
      <div className="fullscreen flex items-center justify-center p-8">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
              {isPhotoMode ? (
                <Camera className="w-10 h-10 text-dark-slate" />
              ) : (
                <Video className="w-10 h-10 text-dark-slate" />
              )}
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">
              Before we start...
            </h2>
            <p className="text-neutral-grey-300 text-lg">
              Add your details (optional), then {isPhotoMode ? 'take your photo' : 'record your message'}.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="guestName" className="block text-white font-medium mb-2 text-lg">
                Your Name (Optional)
              </label>
              <input
                type="text"
                id="guestName"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 text-white rounded-xl focus:outline-none focus:border-gold text-lg placeholder-neutral-grey-400"
                placeholder="Enter your name"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="guestEmail" className="block text-white font-medium mb-2 text-lg">
                Your Email (Optional)
              </label>
              <input
                type="email"
                id="guestEmail"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 text-white rounded-xl focus:border-gold focus:outline-none text-lg placeholder-neutral-grey-400"
                placeholder="your@email.com"
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={resetGuestForm}
                className="flex-1 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all text-lg flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <button
                type="submit"
                className="flex-1 px-8 py-4 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-xl transition-all text-lg shadow-lg shadow-gold/30"
              >
                {isPhotoMode ? 'Open Camera' : 'Start Recording'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fullscreen flex items-center justify-center p-8 relative overflow-hidden"
      style={{
        backgroundImage: `url(${stoneGoldBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-dark-slate/80 via-dark-slate/70 to-black/60" />

      <div className="text-center animate-pulse relative z-10">
        <div className="w-32 h-32 bg-gold rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-gold/50">
          <Heart className="w-16 h-16 text-dark-slate" fill="currentColor" />
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
          {event.eventName}
        </h1>

        <p className="text-2xl text-gold mb-8">
          {eventDate} • {event.location}
        </p>

        {event.welcomeMessage && (
          <p className="text-xl text-neutral-grey-300 max-w-2xl mx-auto mb-12 leading-relaxed">
            {event.welcomeMessage}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            type="button"
            onClick={() => handleSelectMode('video')}
            className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-gold rounded-full text-dark-slate font-bold text-2xl shadow-2xl shadow-gold/50 hover:bg-gold-light transition-all"
          >
            <Video className="w-8 h-8" />
            <span>Record a Video</span>
          </button>
          <button
            type="button"
            onClick={() => handleSelectMode('photo')}
            className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-white/10 border border-white/20 rounded-full text-white font-bold text-2xl shadow-2xl hover:bg-white/20 transition-all"
          >
            <Camera className="w-8 h-8" />
            <span>Take a Photo</span>
          </button>
        </div>

        <p className="text-neutral-grey-500 mt-8 text-lg">
          Choose how you want to share a memory for {event.hostName}
        </p>

        {/* Social Proof - Show when 10+ videos */}
        {event.videoCount >= 10 && (
          <p className="text-neutral-grey-400 mt-4 text-sm">
            Join {event.videoCount} others who left a message!
          </p>
        )}

        {/* Privacy Consent */}
        <p
          className="text-neutral-grey-400 mt-6 text-sm max-w-xl mx-auto leading-relaxed"
          onClick={(e) => e.stopPropagation()}
        >
          By clicking the button above, you agree to our{' '}
          <button
            onClick={() => setShowPrivacyModal(true)}
            className="text-gold hover:text-gold-light underline font-medium transition-colors"
          >
            Data Privacy Policy
          </button>
        </p>
      </div>

      {/* Privacy Modal */}
      <DataPrivacyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </div>
  );
}

export default AttractorScreen;
